import type {
	BrandModel,
	CategoryModel,
	CompetitorsResponseModel,
	EmptyResponse,
	GetBrandsParams,
	GetCategoriesParams,
	HTTPValidationError,
	ListCompetitiveDataParams,
	ListCompetitorsParams,
	ListPriceHistoryParams,
	Preprocessing,
	Setting,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'
import type { AxiosError } from 'axios'
import type { SettingSchemaModel } from '../model/types'

import { useQuery } from '@tanstack/react-query'

import { client } from '@/shared/api/client'

export const coreQueries = {
	brands: (queryParams: GetBrandsParams) => ({
		queryKey: ['brands', queryParams],
		queryFn: () => client.products.getBrands(queryParams),
	}),
	categories: (queryParams: GetCategoriesParams) => ({
		queryKey: ['categories', queryParams],
		queryFn: () => client.products.getCategories(queryParams),
	}),
	competitors: (queryParams: ListCompetitorsParams) => ({
		queryKey: ['competitors', queryParams],
		queryFn: () => client.cd.listCompetitors(queryParams),
	}),
	settingsSchemas: () => ({
		queryKey: ['settings-schemas'],
		queryFn: () =>
			client.settingsSchemas.settingsSchemasList() as Promise<SettingSchemaModel>,
	}),
	prepro: () => ({
		queryKey: ['prepro'],
		queryFn: () => client.api.getPreprocessing(),
	}),
	publicSetting: (settingId: string) => ({
		queryKey: ['public-settings', settingId],
		queryFn: () => client.publicSetting.publicSettingGet({ settingId }),
	}),
	listPriceHistory: (queryParams: ListPriceHistoryParams) => ({
		queryKey: ['price-history', queryParams],
		queryFn: () => client.cd.listPriceHistory(queryParams),
	}),
	listCompetitiveData: (queryParams: ListCompetitiveDataParams) => ({
		queryKey: ['competitive-data', queryParams],
		queryFn: () => client.cd.listCompetitiveData(queryParams),
	}),
}

export const usePublicSettingQuery = <T = Setting>(
	settingId: string,
	options?: UseQueryOptions<Setting, AxiosError, T>
) =>
	useQuery<Setting, AxiosError, T>({
		queryKey: ['public-settings', settingId],
		queryFn: () => client.publicSetting.publicSettingGet({ settingId }),
		...(options || {}),
	})

export const useSettingsSchemasQuery = <T = SettingSchemaModel>(
	options?: UseQueryOptions<SettingSchemaModel, AxiosError, T>
) =>
	useQuery<SettingSchemaModel, AxiosError, T>({
		...coreQueries.settingsSchemas(),
		...(options || {}),
	})
export const useCategoriesQuery = <T = CategoryModel[]>(
	queryParams: GetCategoriesParams = {},
	options?: UseQueryOptions<CategoryModel[], AxiosError<HTTPValidationError>, T>
) =>
	useQuery<CategoryModel[], AxiosError<HTTPValidationError>, T>({
		...coreQueries.categories(queryParams),
		...(options || {}),
	})
export const useBrandsQuery = <T = BrandModel[]>(
	queryParams: GetBrandsParams = {},
	options?: UseQueryOptions<BrandModel[], AxiosError<HTTPValidationError>, T>
) =>
	useQuery<BrandModel[], AxiosError<HTTPValidationError>, T>({
		...coreQueries.brands(queryParams),
		...(options || {}),
	})

export function usePreproQuery<T = Preprocessing>(
	options?: UseQueryOptions<Preprocessing | EmptyResponse, AxiosError, T>
) {
	return useQuery<Preprocessing | EmptyResponse, AxiosError, T>({
		...coreQueries.prepro(),
		...(options || {}),
	})
}
export const useCompetitorsOptionsQuery = <T = CompetitorsResponseModel>(
	params: ListCompetitorsParams,
	options?: UseQueryOptions<CompetitorsResponseModel, AxiosError, T>
) =>
	useQuery<CompetitorsResponseModel, AxiosError, T>({
		...coreQueries.competitors(params),
		...(options || {}),
	})
